export type EnumDictionary<T extends string | symbol | number, U> = {
	[K in T]: U;
};

export type Nullable<T> = T | null | undefined;

type TranslationProductList = {
	title: string
	description?: Nullable<string>
	elements: string[]
}

export type Translation = {
	header: {
		contactButtonText: string
	},
	pages: {
		main: string
		products: string
		aboutUs: string
	}
	products: {
		stretch: TranslationProduct
		hand: TranslationProduct
		machine: TranslationProduct
		miniRap: TranslationProduct
		preStretch: TranslationProduct
		hood: TranslationProduct
		accessories: TranslationProduct
	}
	mainPage: {
		hero: {
			title: {
				part1: string
				part2: string
			}
			description: string
			buttonText: string
		}
		features: {
			protection: {
				title: string
				description: string
			}
			userSupport: {
				title: string
				description: string
			}
			partnership: {
				title: string
				description: string
			}
			application: {
				title: string
				description: string
			}
			elastic: {
				title: string
				description: string
			}
			quality: {
				title: string
				description: string
			}
		}
		popularProducts: {
			title: {
				part1: string
				part2: string
			}
			description: string
			seeAllProductsButtonText: string
		}
		technologyPartner: {
			title: string
		}
		whyUs: {
			title: string
			description: string
			elements: ListElement[]
		}
	}
	productsPage: {
		header: {
			title: string
			description: string
		}
		productsList: {
			title: string
		}
	}
	aboutUsPage: {
		header: {
			title: string
			description: string
		}
		content: {
			title: string
			text: {
				part1: string
				part2?: string
			}
		}
		banner: {
			title: string
			text: string
		}
		list: {
			title: string
			elements: string[]
		}
	}
	contactModal: {
		title: string
		text: string
		callButton: string
		buttonsMiddleText: string
		emailButton: string
	}
	CTA: {
		title: string
		text: string
		callButton: string
		buttonsMiddleText: string
		emailButton: string
	}
	snackbar: {
		copyMessage: {
			phone: string
			email: string
		}
	}
	footer: {
		columnNames: {
			contact: string
			pages: string
			products: string
		}
	}
}

export type TranslationProduct = {
	title: string
	description: string
	basicInformation: {
		title: string
		textOne: string
		textTwo?: string
		list1?: TranslationProductList
		list2?: TranslationProductList
	}
	highlights?: {
		title: string
		elements: ListElement[]
	}
	usage?: {
		title: string
		elements: string[]
	}
	table?: {
		title: string
		bottomText: string
		tableContent: string[][]
	}
	bigTextGradient: string
	bigText: string
}

export enum Color {
	BLUE = "sky-400",
	PURPLE = "purple-600",
	EMERALD = "emerald-300",
	DARK_BLUE = "gray-900",
	NEUTRAL = "neutral-700",
}

export enum ButtonHoverColor {
	BLUE = "sky-500",
	PURPLE = "purple-700",
	EMERALD = "emerald-400",
	DARK_BLUE = "gray-800",
	NEUTRAL = "neutral-800",
}

export enum ButtonSize {
	LARGE = "LARGE",
	MEDIUM = "MEDIUM",
	SMALL = "SMALL",
}

export type Product = {
	id: number
	textId: string
	name: string
	image: string
	basicInformationImage?: string
	banner?: string
	gallery?: string[]
}

export type SpecificationType = {
	titles: SpecificationTitleType[]
	columns: SpecificationColumnType[]
}

export type HighlightItem = {
	textId: string
}

export type SpecificationTitleType = {
	id: string
}

export type SpecificationColumnType = {
	id: string
}

export type BasicInformationType = {
	image: string
	title: string
	textOne: string
	textTwo?: string
}

export type HighlightsType = {
	title: string
	items: HighlightItem[]
}

export type UsageType = {
	title: string
	text: string
	bigTextGradient?: string
	bigText?: string
}

export type FooterColumnType = {
	id: string
	columnItems: FooterColumnItem[]
}

export type FooterColumnItem = {
	id: string
	image?: string
	text?: string
	link: string
}

export enum TimelineSide {
	LEFT = "LEFT",
	RIGHT = "RIGHT",
}

export type TimelineItemType = {
	number: number
	id: string
	side: TimelineSide
}

export enum Locales {
	PL = "pl",
	EN = "en",
}

export type TooltipProps = {
	message: string
	id: string
}

export enum SnackbarType {
	SUCCESS = "SUCCESS",
	FAILURE = "FAILURE",
}

export type ListElement = {
	title: string
	description: string
}